import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { getErrorMessage } from '../utils/errors';
import MarkdownRenderer from '../components/ui/MarkdownRenderer';
import ConfirmModal from '../components/ui/ConfirmModal';

import { useAuth } from '../context/AuthContext';

const DEFAULTS = [
  { name: 'Personality', icon: '🧠', color: '#6b8c6b', description: 'Who am I becoming as a person?' },
  { name: 'Career', icon: '💼', color: '#c9a84c', description: 'Professional growth and skills' },
  { name: 'Emotional', icon: '❤️', color: '#c4623a', description: 'Emotional health and awareness' },
  { name: 'Relationships', icon: '🤝', color: '#5b8ba8', description: 'Connections that matter to me' },
  { name: 'Physical', icon: '💪', color: '#8b6bc4', description: 'Body, health, and fitness' },
];

export default function Categories() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const activeColors = user?.category_colors || ['#6b8c6b', '#c9a84c', '#c4623a', '#5b8ba8', '#8b6bc4', '#c46b8b', '#6bc4b8', '#a8895b'];
  const activeIcons = user?.category_icons || ['🧠', '💼', '❤️', '🤝', '💪', '🎯', '📚', '🌿', '💰', '🎨', '🙏', '⚡'];
  const [categories, setCategories] = useState([]);
  const [archived, setArchived] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [form, setForm] = useState({ name: '', icon: '🧠', color: '#6b8c6b', description: '' });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    const [active, arch, temps] = await Promise.all([
      API.get('/categories'),
      API.get('/categories?include_archived=true'),
      API.get('/categories/templates'),
    ]);
    setCategories(active.data);
    setArchived(arch.data.filter(c => c.archived));
    setTemplates(temps.data);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditCat(null);
    setForm({ name: '', icon: '🧠', color: '#6b8c6b', description: '' });
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setForm({ name: cat.name, icon: cat.icon, color: cat.color, description: cat.description || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setLoading(true);
    try {
      if (editCat) {
        await API.put(`/categories/${editCat.id}`, form);
        toast.success(`${form.icon} ${form.name} updated!`);
      } else {
        await API.post('/categories', form);
        toast.success(`${form.icon} ${form.name} created!`);
      }
      setShowModal(false);
      setForm({ name: '', icon: '🧠', color: '#6b8c6b', description: '' });
      setEditCat(null);
      load();
      window.location.reload();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to save category')); }
    finally { setLoading(false); }
  };

  const handleArchive = (id, name) => {
    setConfirm({
      title: 'Archive Category?',
      message: `Are you sure you want to archive "${name}"? You can restore it anytime from the archived section.`,
      confirmLabel: 'Archive',
      onConfirm: async () => {
        try {
          await API.delete(`/categories/${id}`);
          toast.success('Category archived');
          load();
          window.location.reload();
        } catch (e) { toast.error(getErrorMessage(e, 'Failed to archive')); }
      }
    });
  };

  const handlePermanentDelete = (id, name) => {
    setConfirm({
      title: 'Delete Permanently?',
      message: `WARNING: This will permanently delete "${name}" and ALL its associated daily logs and goals. This cannot be undone.`,
      confirmLabel: 'Delete Forever',
      danger: true,
      onConfirm: async () => {
        try {
          await API.delete(`/categories/${id}?permanent=true`);
          toast.success(`"${name}" and all its records deleted.`);
          load();
          window.location.reload();
        } catch (e) { toast.error(getErrorMessage(e, 'Failed to delete')); }
      }
    });
  };

  const handleRestore = async (id, name) => {
    await API.put(`/categories/${id}/restore`);
    toast.success(`${name} restored!`);
    load();
    window.location.reload();
  };

  const addDefault = async (def) => {
    try {
      await API.post('/categories', def);
      toast.success(`${def.icon} ${def.name} added!`);
      load();
      window.location.reload();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to add category')); }
  };

  const handleSaveAsTemplate = async (cat) => {
    try {
      await API.post('/categories/templates', { name: cat.name, icon: cat.icon, color: cat.color, description: cat.description || '' });
      toast.success('Saved to your templates!');
      load();
    } catch { toast.error('Failed to save template'); }
  };

  const handleDeleteTemplate = (id) => {
    setConfirm({
      title: 'Delete Template?',
      message: 'Are you sure you want to remove this category template?',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await API.delete(`/categories/templates/${id}`);
          toast.success('Template deleted');
          load();
        } catch { toast.error('Failed to delete template'); }
      }
    });
  };

  return (
    <div>
      <div className="page-header">
        <h1>Categories</h1>
        <p>The arenas of your life you want to grow in</p>
      </div>

      <div className="page-body">
        {categories.length === 0 && (
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ marginBottom: 8, fontSize: 18 }}>Start with defaults</h3>
            <p style={{ fontSize: 14, color: 'rgba(13,13,13,0.5)', marginBottom: 20 }}>
              Quick-add the most common life categories, or create your own below.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {DEFAULTS.map((d) => (
                <button key={d.name} className="btn btn-outline" onClick={() => addDefault(d)}>
                  {d.icon} {d.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="stack-on-mobile" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontFamily: 'Fraunces', fontSize: 20 }}>Your Categories ({categories.length})</h3>
          <div className="wrap-on-mobile" style={{ display: 'flex', gap: 8 }}>
            {archived.length > 0 && (
              <button className={`btn btn-sm ${showArchived ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setShowArchived(!showArchived)}>
                {showArchived ? 'Hide' : 'Show'} Archived ({archived.length})
              </button>
            )}
            <button className="btn btn-primary" onClick={openCreate}>+ Add Category</button>
          </div>
        </div>

        {categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">▦</div>
            <h3>No categories yet</h3>
            <p>Create your first category to start tracking growth across different areas of your life.</p>
          </div>
        ) : (
          <div className="grid-3">
            {categories.map((cat) => (
              <div
                key={cat.id}
                className="card"
                onClick={() => navigate(`/categories/${cat.id}`)}
                style={{
                  borderTop: `4px solid ${cat.color}`,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <span style={{ fontSize: 32 }}>{cat.icon}</span>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleSaveAsTemplate(cat)} title="Save as Template" style={{ color: 'var(--sage)' }}>⭐</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(cat)} title="Edit" style={{ color: 'rgba(13,13,13,0.4)' }}>✎</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleArchive(cat.id, cat.name)} title="Archive" style={{ color: 'rgba(13,13,13,0.3)' }}>📦</button>
                  </div>
                </div>
                <h3 style={{ fontSize: 18, marginBottom: 4 }}>{cat.name}</h3>
                {cat.description && (
                  <div className="markdown-body" style={{ fontSize: 13, color: 'rgba(13,13,13,0.5)', marginBottom: 12, pointerEvents: 'none' }}>
                    <MarkdownRenderer content={cat.description} />
                  </div>
                )}
                <div style={{ marginTop: 'auto', fontSize: 12, color: 'var(--sage)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  View Detail & Logs <span>→</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Archived */}
        {showArchived && archived.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <h3 style={{ fontFamily: 'Fraunces', fontSize: 18, marginBottom: 4, color: 'rgba(13,13,13,0.5)' }}>
              Archived Categories
            </h3>
            <p style={{ fontSize: 13, color: 'rgba(13,13,13,0.4)', marginBottom: 16 }}>
              Archived categories can be restored or permanently deleted along with all their data.
            </p>
            <div className="grid-3">
              {archived.map((cat) => (
                <div key={cat.id} className="card" style={{ borderTop: `3px solid ${cat.color}`, opacity: 0.75 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <span style={{ fontSize: 32 }}>{cat.icon}</span>
                    <div style={{ display: 'flex', gap: 4, flexDirection: 'column', alignItems: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleRestore(cat.id, cat.name)}
                        style={{ fontSize: 11, color: 'var(--sage)', border: '1px solid var(--sage)', borderRadius: 6, padding: '3px 8px' }}>
                        Restore
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handlePermanentDelete(cat.id, cat.name)}
                        style={{ fontSize: 11, color: 'var(--rust)', border: '1px solid var(--rust)', borderRadius: 6, padding: '3px 8px' }}>
                        Delete Forever
                      </button>
                    </div>
                  </div>
                  <h3 style={{ fontSize: 18, marginBottom: 4 }}>{cat.name}</h3>
                  {cat.description && <div className="markdown-body" style={{ fontSize: 13, color: 'rgba(13,13,13,0.4)' }}><MarkdownRenderer content={cat.description} /></div>}
                  <span className="tag tag-mist" style={{ fontSize: 11, marginTop: 8, display: 'inline-block' }}>Archived</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editCat ? 'Edit Category' : 'New Category'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>

            {!editCat && (
              <div style={{ marginBottom: 20 }}>
                <label className="form-label" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(13,13,13,0.4)' }}>Quick Templates</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  <span style={{ fontSize: 10, width: '100%', color: 'rgba(13,13,13,0.3)', marginBottom: 2 }}>DEFAULTS:</span>
                  {DEFAULTS.map((d) => (
                    <button key={d.name} type="button" onClick={() => setForm({ ...d })}
                      className="template-chip">
                      <span>{d.icon}</span> {d.name}
                    </button>
                  ))}
                </div>
                {templates.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <span style={{ fontSize: 10, width: '100%', color: 'var(--sage)', marginBottom: 2 }}>MY TEMPLATES:</span>
                    {templates.map((t) => (
                      <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <button type="button" onClick={() => setForm({ name: t.name, icon: t.icon, color: t.color, description: t.description })}
                          className="template-chip" style={{ border: '1px solid var(--sage)' }}>
                          <span>{t.icon}</span> {t.name}
                        </button>
                        <button onClick={() => handleDeleteTemplate(t.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(13,13,13,0.2)', fontSize: 10 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Name</label>
              <input maxLength={200} className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Spiritual Growth" />
            </div>
            <div className="form-group">
              <label className="form-label">Description (optional)</label>
              <input maxLength={200} className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does this area mean to you?" />
              <div style={{ fontSize: 11, color: 'rgba(13,13,13,0.4)', marginTop: 4 }}>Markdown supported</div>
            </div>
            <div className="form-group">
              <label className="form-label">Icon</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {activeIcons.map((ic) => (
                  <button key={ic} onClick={() => setForm({ ...form, icon: ic })}
                    style={{ width: 40, height: 40, borderRadius: 8, border: `2px solid ${form.icon === ic ? 'var(--sage)' : 'rgba(13,13,13,0.1)'}`, background: form.icon === ic ? 'rgba(107,140,107,0.1)' : 'white', cursor: 'pointer', fontSize: 20 }}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {activeColors.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })}
                    style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: `3px solid ${form.color === c ? 'var(--ink)' : 'transparent'}`, cursor: 'pointer' }} />
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 8, paddingLeft: 16, borderLeft: '1px solid rgba(13,13,13,0.1)' }}>
                  <span style={{ fontSize: 11, color: 'rgba(13,13,13,0.4)', textTransform: 'uppercase' }}>Custom:</span>
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    style={{ width: 32, height: 32, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  />
                  <input
                    type="text"
                    className="form-input input-sm"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    style={{ height: 32, fontSize: 12, padding: '0 8px', fontFamily: 'monospace' }}
                  />
                </div>
              </div>
            </div>
            <div className="stack-on-mobile" style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button className="btn btn-outline" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={loading} style={{ flex: 1 }}>
                {loading ? 'Saving...' : editCat ? 'Save Changes' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal config={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
